import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import MSINav from "./MSINav";
import { useEntitlements } from "./entitlements/EntitlementContext";
import { listGuruPlays } from "./api/guruPlaymaker";
import GuruPlayMakerEditor from "./components/GuruPlayMakerEditor";
import GuruPlayMakerResult from "./components/GuruPlayMakerResult";
import { selectCurrentGuruPlay } from "./guruPlayLifecycle";

const panel = { background: "#171717", border: "1px solid #2A2A2A", borderRadius: 12, padding: 18 };
const colorForBias = (bias) => bias === "CALL" ? "#22c55e" : bias === "PUT" ? "#ef4444" : "#f59e0b";
const tvUrl = (ticker) => `https://www.tradingview.com/chart/?symbol=${encodeURIComponent(ticker)}`;

function fmt(n, d = 2) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "-";
  return Number(n).toFixed(d);
}
function Row({ label, value }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", borderBottom: "1px solid #242424", padding: "8px 0", gap: 16 }}>
      <span style={{ color: "#A0A0A0" }}>{label}</span>
      <span style={{ color: "white", textAlign: "right" }}>{value ?? "-"}</span>
    </div>
  );
}
function fmtTarget(value) {
  if (!value) return "-";

  const text = String(value);

  if (text.includes("No overhead target")) return "No overhead target";
  if (text.includes("No downside target")) return "No downside target";

  return text.replace(/@\\s*([0-9.]+)/, (_, n) => `@ ${Number(n).toFixed(2)}`);
}

function patternWithScore(label, score) {
  if (!label || label === "-" || label === "NONE") return "None";
  const n = Number(score);
  return Number.isNaN(n) || n <= 0 ? label : `${label} (${Math.round(n)})`;
}


function chartFamily(row) {
  const types = [
    row.weekly_chart_pattern_type,
    row.tf3d_chart_pattern_type,
    row.daily_chart_pattern_type,
    row.tf4h_chart_pattern_type,
  ]
    .map((x) => String(x || "").toUpperCase())
    .filter((x) => x && x !== "NONE" && x !== "-");

  if (!types.length) return "-";

  const counts = types.reduce((acc, x) => {
    acc[x] = (acc[x] || 0) + 1;
    return acc;
  }, {});

  const ranked = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  if (ranked.length > 1 && ranked[0][1] === ranked[1][1]) return "MIXED";
  return ranked[0][0];
}

function compactThemes(theme) {
  if (!theme) return "-";
  const parts = String(theme).split("|").filter(Boolean);
  if (parts.length <= 2) return parts.join(", ");
  return `${parts.slice(0, 2).join(", ")} +${parts.length - 2}`;
}

function msiSourceSnapshot(row) {
  const sourceRisk = ["LOW", "MEDIUM", "HIGH"].includes(String(row?.source_risk || row?.risk_level || "").toUpperCase())
    ? String(row.source_risk || row.risk_level).toUpperCase() : null;
  const flags = Array.isArray(row?.risk_flags) ? row.risk_flags.map((value) => String(value).toUpperCase()) : [];
  return {
    source: "MSI", source_decision: row?.setup_type || row?.bias || null, source_risk: sourceRisk,
    source_risk_flags: flags,
    structured_risk: { vix_regime: row?.vix_regime || null, vix_state: row?.vix_state || null },
  };
}

export default function MSITickerDetail() {
  const { ticker } = useParams();
  const { isAdmin, hasGuruPlaysAccess } = useEntitlements();
  const [rows, setRows] = useState([]);
  const [guruPlays, setGuruPlays] = useState([]);
  const [showPlayMaker, setShowPlayMaker] = useState(false);
  const [editingPlay, setEditingPlay] = useState(null);

    useEffect(() => {
      fetch(`/data/market-desk.json?t=${Date.now()}`, {
        cache: "no-store",
        headers: {
          "Cache-Control": "no-cache"
        }
      })
        .then((r) => r.json())
        .then((data) => setRows(Array.isArray(data) ? data : [data]))
        .catch((err) => console.error("MSI detail load failed:", err));
    }, []);

  const canViewGuruPlays = isAdmin || hasGuruPlaysAccess("MSI");
  const currentGuruPlay = selectCurrentGuruPlay(guruPlays.filter((play) => play.publication_state === "PUBLISHED" || isAdmin));
  const refreshGuruPlays = () => canViewGuruPlays ? listGuruPlays({ source: "MSI", ticker, includeDrafts: isAdmin }).then(setGuruPlays).catch(() => setGuruPlays([])) : Promise.resolve(setGuruPlays([]));
  useEffect(() => { refreshGuruPlays(); }, [ticker, isAdmin, canViewGuruPlays]);

  const r = useMemo(() => {
  const matches = rows.filter(
    (x) => String(x.ticker).toUpperCase() === String(ticker).toUpperCase()
  );

  return matches.sort((a, b) => {
    const aScore = Number(a.msi_score || a.confidence || 0);
    const bScore = Number(b.msi_score || b.confidence || 0);
    const aCandle = a.candle_score == null ? 0 : 1;
    const bCandle = b.candle_score == null ? 0 : 1;

    return bCandle - aCandle || bScore - aScore;
  })[0];
}, [rows, ticker]);

  if (!r) {
    return <div style={{ minHeight: "100vh", background: "#0B0B0B", color: "white", padding: 24 }}>Loading {ticker}...</div>;
  }

  return (
    <div style={{ minHeight: "100vh", background: "#0B0B0B", color: "white", padding: 24 }}>
      <a href="/msi/universe" style={{ color: "#A0A0A0", textDecoration: "none" }}>← Back to Universe</a>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "end", marginTop: 16 }}>
        <div>
          <h1 style={{ fontSize: 48, marginBottom: 4 }}>{r.ticker}</h1>
          <div style={{ color: "#A0A0A0" }}>{r.sector || "-"} {r.theme ? `• ${compactThemes(r.theme)}` : ""}</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 36 }}>${fmt(r.price)}</div>
          <div style={{ color: colorForBias(r.bias), fontWeight: 800 }}>
            {r.bias} • MSI {Math.round(Number(r.msi_score || 0))} • Candle {r.candle_score ?? "-"} • Chart {r.chart_pattern_score ?? "-"}
          </div>
        </div>
      </div>
      <MSINav />

      {canViewGuruPlays && currentGuruPlay ? <div key={currentGuruPlay.id} style={{marginBottom:12}}><GuruPlayMakerResult result={currentGuruPlay} onView={isAdmin ? () => { setEditingPlay(currentGuruPlay); setShowPlayMaker(true); } : undefined} /></div> : null}
      {isAdmin && <button type="button" onClick={() => { setEditingPlay(null); setShowPlayMaker((value) => !value); }} style={{marginBottom:12}}>OPEN GURU PLAYMAKER</button>}
      {showPlayMaker && isAdmin && <div style={{marginBottom:18}}><GuruPlayMakerEditor key={editingPlay?.id || "new"} play={editingPlay} request={{ source:"MSI", ticker:r.ticker, direction:r.bias, underlying_price:Number(r.price), setup_type:r.setup_type, confidence:Number(r.msi_score || r.confidence || 0), trigger:r.trigger, target:r.target, invalidation:r.invalidation, attack_levels:[], source_metadata:{source_snapshot:msiSourceSnapshot(r)} }} onSaved={refreshGuruPlays} onClose={() => { setShowPlayMaker(false); setEditingPlay(null); }} /></div>}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 20 }}>
        <div style={panel}>
          <h2>Structure</h2>
          <Row label="Setup" value={r.setup_type} />
          <Row label="Market State" value={r.market_state} />
          <Row label="3D Stage" value={r.recovery_3d_stage} />
          <Row label="3D Score" value={fmt(r.recovery_3d_score, 0)} />
          <Row label="Weekly Trend" value={r.weekly_trend} />
          <Row label="Weekly Score" value={fmt(r.weekly_sustainability_score, 0)} />
          <Row label="Expansion Room" value={r.recovery_3d_expansion_room_pct == null ? "-" : `${fmt(r.recovery_3d_expansion_room_pct, 1)}%`} />
        </div>
        <div style={panel}>
          <h2>Candlestick Structure</h2>
          <Row label="Weekly" value={r.weekly_candle_label} />
          <Row label="3D" value={r.tf3d_candle_label} />
          <Row label="Daily" value={r.daily_candle_label} />
          <Row label="4H" value={r.tf4h_candle_label} />
          <Row label="Candle Bias" value={r.candle_bias} />
          <Row label="Alignment" value={r.pattern_alignment} />
          <Row label="Candle Score" value={r.candle_score} />
        </div>

        <div style={panel}>
          <h2>Chart Structure</h2>
          <Row label="Weekly" value={patternWithScore(r.weekly_chart_pattern_label, r.weekly_chart_pattern_score)} />
          <Row label="3D" value={patternWithScore(r.tf3d_chart_pattern_label, r.tf3d_chart_pattern_score)} />
          <Row label="Daily" value={patternWithScore(r.daily_chart_pattern_label, r.daily_chart_pattern_score)} />
          <Row label="4H" value={patternWithScore(r.tf4h_chart_pattern_label, r.tf4h_chart_pattern_score)} />
          <Row label="Pattern Family" value={chartFamily(r)} />
          <Row label="Alignment" value={r.chart_pattern_alignment} />
          <Row label="Chart Score" value={r.chart_pattern_score} />
        </div>
        <div style={panel}>
          <h2>Trade Plan</h2>
          <Row label="Trigger" value={r.trigger} />
          <Row label="Target" value={fmtTarget(r.target)} />
          <Row label="Invalidation" value={r.invalidation} />
          <Row label="VIX Regime" value={r.vix_regime} />
          <Row label="VIX State" value={r.vix_state} />
          <Row label="Confluence" value={fmt(r.confluence_score, 0)} />
          <Row label="Confluence Zone" value={r.confluence_zone} />
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginTop: 20 }}>
        <div style={panel}>
          <h2>Key Levels</h2>
          <Row label="4H 10MA" value={fmt(r.ma4h_10)} />
          <Row label="4H 20MA" value={fmt(r.ma4h_20)} />
          <Row label="4H 50MA" value={fmt(r.ma4h_50)} />
          <Row label="4H 200MA" value={fmt(r.ma4h_200)} />
          <Row label="3D 50MA" value={fmt(r.ma3d_50)} />
          <Row label="3D 200MA" value={fmt(r.ma3d_200)} />
          <Row label="Weekly 50MA" value={fmt(r.weekly_50)} />
          <Row label="Weekly 200MA" value={fmt(r.weekly_200)} />
        </div>
        <div style={panel}>
          <h2>Reason</h2>
          <p style={{ color: "#D4D4D4", lineHeight: 1.6 }}>{r.reason || "-"}</p>
          <h3>Pattern Summary</h3>
          <p style={{ color: "#D4D4D4", lineHeight: 1.6 }}>{r.pattern_summary || "-"}</p>

          <h3>Chart Pattern Summary</h3>
          <p style={{ color: "#D4D4D4", lineHeight: 1.6 }}>{r.chart_pattern_summary || "-"}</p>

        </div>
      </div>

      <div style={{ ...panel, marginTop: 20 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 16,
            marginBottom: 12,
          }}
        >
          <h2 style={{ margin: 0 }}>TradingView Chart</h2>

          <a
            href={tvUrl(r.ticker)}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: "#22c55e",
              fontWeight: 800,
              textDecoration: "none",
            }}
          >
            Open Full Chart ↗
          </a>
        </div>

        <iframe
          title={`${r.ticker} TradingView Chart`}
          src={`https://www.tradingview.com/widgetembed/?symbol=${encodeURIComponent(
            r.ticker
          )}&interval=240&theme=dark&style=1&timezone=America%2FChicago&withdateranges=1&hide_side_toolbar=0&allow_symbol_change=1&save_image=0`}
          style={{
            width: "100%",
            height: "720px",
            border: "none",
            borderRadius: 12,
            background: "#111",
          }}
        />
      </div>
    </div>
  );
}
